import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Countdown, AuthImage, fmtINR } from "@/components/common";
import Uploader from "@/components/Uploader";

export default function PaymentConfirm({ segment, orderId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [reason, setReason] = useState("");
  const [fileId, setFileId] = useState(null);
  const [expired, setExpired] = useState(false);

  const received = async () => {
    setBusy(true);
    try {
      await api.post("/customer/confirm", { segment_id: segment.segment_id, action: "received" });
      toast.success("Payment confirmed. Thank you!");
      onDone && onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const notReceived = async () => {
    setBusy(true);
    try {
      await api.post("/customer/confirm", { segment_id: segment.segment_id, action: "not_received", reason, file_id: fileId });
      toast.success("Dispute raised. Our team will review it.");
      onDone && onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 via-white to-amber-50 p-5 shadow-sm"
      data-testid={`payment-confirm-${segment.segment_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-amber-700">Payment sent — please confirm</div>
          <div className="mt-1 font-heading font-bold text-xl text-slate-900">{fmtINR(segment.amount)}</div>
          <div className="mt-1 text-sm text-slate-600 space-y-0.5">
            <div>Order <span className="font-mono">{orderId}</span></div>
            <div>Segment <span className="font-mono">{segment.segment_id}</span></div>
            <div>UTR / Ref <span className="font-mono">{segment.reference}</span></div>
            <div>Sent at {new Date(segment.payment_sent_at).toLocaleString()}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500">Time left</div>
          <Countdown expiry={segment.expiry_at} onExpire={() => setExpired(true)} testid={`payment-countdown-timer-${segment.segment_id}`} />
        </div>
      </div>

      {segment.file_id && (
        <div className="mt-3">
          <div className="text-xs text-slate-500 mb-1">Payment proof</div>
          <AuthImage fileId={segment.file_id} className="max-h-40 rounded-lg border border-slate-200" />
        </div>
      )}

      {expired ? (
        <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
          The 10-minute window has ended. This payment is now under review — please refresh. This does not mean the payment was received.
        </div>
      ) : !showDispute ? (
        <div className="mt-4 flex gap-3">
          <button onClick={received} disabled={busy} data-testid={`payment-received-button-${segment.segment_id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Payment Received
          </button>
          <button onClick={() => setShowDispute(true)} disabled={busy} data-testid={`payment-not-received-button-${segment.segment_id}`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-rose-300 text-rose-700 font-semibold hover:bg-rose-50 transition">
            <AlertTriangle className="w-4 h-4" /> Payment Not Received
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Describe the issue (required)" data-testid={`dispute-reason-${segment.segment_id}`}
            className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:border-blue-500 outline-none text-sm" />
          <Uploader fileId={fileId} onUploaded={setFileId} testid={`dispute-upload-${segment.segment_id}`} label="Attach evidence (optional)" />
          <div className="flex gap-3">
            <button onClick={notReceived} disabled={busy || !reason.trim()} data-testid={`dispute-submit-${segment.segment_id}`}
              className="flex-1 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 transition disabled:opacity-60">
              Submit dispute
            </button>
            <button onClick={() => setShowDispute(false)} className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
